package com.yendo.mx.ui.cart

import com.yendo.mx.utils.BackHandler
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.models.CartItem
import com.yendo.mx.models.CartManager
import com.yendo.mx.models.Producto
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.formatDecimal
import com.yendo.mx.utils.formatPrecio
import com.yendo.mx.utils.getAhora
import kotlin.math.roundToInt

import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.DayOfWeek

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import coil3.size.Size

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CarritoScreen(
    onBack: () -> Unit,
    esInvitado: Boolean,
    onSugerirRegistro: () -> Unit,
    direccionActual: String,
    latitudCliente: Double,
    longitudCliente: Double,
    metodosBloqueadosCliente: String,
    onPedidoConfirmado: (String) -> Unit
) {
    val isDark = isSystemInDarkTheme()
    val colorFondoPantalla = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorFondoComponentes = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    var irACheckout by remember { mutableStateOf(false) }
    val subtotal = CartManager.totalDinero

    // 🚀 BACKHANDLER: Botón físico de Android
    BackHandler { onBack() }

    // 🚀 ESTADO: Preferencia de Reemplazo por Defecto
    var accionAgotado by remember { mutableStateOf("LLAMAR") }

    val zonaConfig = remember { ConfigManager.configActiva }
    val globalProgramadoActivo = zonaConfig?.operacion?.programados_activos ?: true

    val horariosRestaurante = CartManager.restauranteActualHorarios
    val programadoActivo = (CartManager.restauranteActualProgramadoActivo ?: false) && globalProgramadoActivo

    val tiempoActualMs = remember { getAhora() }
    val pausaHasta = CartManager.restauranteActualPausaHasta ?: 0L
    val estaEnPanico = CartManager.restauranteActualPausadoManualmente == true && pausaHasta > tiempoActualMs

    val estaAbiertoNormal = remember(horariosRestaurante) {
        if (horariosRestaurante == null) return@remember true
        val ahora = Instant.fromEpochMilliseconds(getAhora()).toLocalDateTime(TimeZone.currentSystemDefault())
        val diaActual = ahora.dayOfWeek
        val horaActualStr = "${ahora.hour.toString().padStart(2, '0')}:${ahora.minute.toString().padStart(2, '0')}"

        val franjasDeHoy = when(diaActual) {
            DayOfWeek.MONDAY -> horariosRestaurante.lunes
            DayOfWeek.TUESDAY -> horariosRestaurante.martes
            DayOfWeek.WEDNESDAY -> horariosRestaurante.miercoles
            DayOfWeek.THURSDAY -> horariosRestaurante.jueves
            DayOfWeek.FRIDAY -> horariosRestaurante.viernes
            DayOfWeek.SATURDAY -> horariosRestaurante.sabado
            DayOfWeek.SUNDAY -> horariosRestaurante.domingo
        }

        val franjasActivas = franjasDeHoy.filter { it.activo }.sortedBy { it.abre }
        franjasActivas.any { horaActualStr >= it.abre && horaActualStr <= it.cierra }
    }

    // 🚀 SINCRONIZACIÓN EN TIEMPO REAL: Evita datos obsoletos de horarios o cierres de emergencia
    LaunchedEffect(CartManager.restauranteActualId) {
        val idRest = CartManager.restauranteActualId
        if (idRest != null) {
            try {
                val snapshot = dev.gitlive.firebase.Firebase.firestore
                    .collection("Negocios")
                    .document(idRest)
                    .get()
                
                if (snapshot.exists) {
                    val restActualizado = snapshot.data<com.yendo.mx.models.RestauranteFirebase>().copy(id = snapshot.id)
                    CartManager.sincronizarMetadata(restActualizado)
                    println("YENDO_LOG: Metadata del restaurante $idRest sincronizada en Carrito.")
                }

                // 🚀 2. SINCRONIZACIÓN DE PRODUCTOS (PRECIOS Y ESCALAS)
                val idsProductos = CartManager.items.map { it.producto.id }.distinct()
                if (idsProductos.isNotEmpty()) {
                    val productsSnapshot = dev.gitlive.firebase.Firebase.firestore
                        .collection("Productos")
                        .where { dev.gitlive.firebase.firestore.FieldPath.documentId inArray idsProductos }
                        .get()
                    
                    val freshProducts = productsSnapshot.documents.map { 
                        it.data<com.yendo.mx.models.Producto>().copy(id = it.id) 
                    }
                    CartManager.sincronizarProductos(freshProducts)
                    println("YENDO_LOG: ${freshProducts.size} productos sincronizados en Carrito.")
                }

            } catch (e: Exception) {
                println("YENDO_LOG ERROR: Fallo al sincronizar en Carrito -> ${e.message}")
                e.printStackTrace()
            }
        }
    }

    val operacionActiva = estaAbiertoNormal && !estaEnPanico
    val deliveryActivoComercio = CartManager.restauranteActualDeliveryActivo ?: true
    val deliveryActivoZona = zonaConfig?.logistica?.delivery_activo ?: true
    val deliveryPropio = CartManager.restauranteActualDeliveryPropioActivo ?: false
    val deliveryActivo = deliveryActivoComercio && (deliveryActivoZona || deliveryPropio)
    val pickupActivo = (zonaConfig?.logistica?.pickup_activo ?: true) && (CartManager.restauranteActualPickupActivo ?: true)
    val hayLogisticaDisponible = deliveryActivo || pickupActivo

    // 🚀 VALIDACIÓN 1: Seguridad básica (Incluye validación de adicionales agotados)
    val hayAgotados = CartManager.items.any { 
        !it.producto.disponible || it.adicionales.any { adic -> !adic.disponible } 
    }
    val permiteIrAPagarSeguridad = CartManager.items.isNotEmpty() && (operacionActiva || (!estaEnPanico && programadoActivo)) && hayLogisticaDisponible && !hayAgotados

    // 🚀 VALIDACIÓN MAESTRA
    val botonHabilitado = permiteIrAPagarSeguridad

    if (irACheckout) {
        CheckoutScreen(
            onBack = { irACheckout = false },
            total = subtotal,
            direccionActual = direccionActual,
            latitudCliente = latitudCliente,
            longitudCliente = longitudCliente,
            metodosBloqueadosCliente = metodosBloqueadosCliente,
            // 🚀 PASAMOS LAS VARIABLES DE NEGOCIO AL CHECKOUT
            accionAgotado = accionAgotado,
            pagoPostArmado = CartManager.restauranteActualPagoPostArmado ?: false,
            onPedidoConfirmado = { idGenerado ->
                irACheckout = false
                onPedidoConfirmado(idGenerado)
            }
        )
    } else {
        Scaffold(
            modifier = Modifier.fillMaxSize(),
            containerColor = colorFondoPantalla,
            topBar = {
                TopAppBar(
                    title = { Text("Tu Pedido", fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal) },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Volver", tint = colorTextoPrincipal)
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoComponentes)
                )
            },
            bottomBar = {
                Surface(
                    shadowElevation = 16.dp,
                    color = colorFondoComponentes,
                    shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .navigationBarsPadding()
                            .padding(20.dp)
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Subtotal", fontSize = 16.sp, color = colorTextoSecundario, fontWeight = FontWeight.Bold)
                            Text("$${formatPrecio(subtotal)}", fontWeight = FontWeight.ExtraBold, fontSize = 22.sp, color = RosaYendo)
                        }

                        // 🚀 CEREBRO DEL BOTÓN (Mensajes Dinámicos)
                        val textoBoton = when {
                            !hayLogisticaDisponible -> "Sin métodos de entrega"
                            estaEnPanico -> "Pausado temporalmente"
                            !permiteIrAPagarSeguridad -> "Restaurante cerrado"
                            esInvitado -> "Regístrate para comprar"
                            else -> "Ir a Pagar"
                        }

                        Button(
                            onClick = {
                                if (esInvitado) onSugerirRegistro() else irACheckout = true
                            },
                            modifier = Modifier.fillMaxWidth().height(56.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = if (!botonHabilitado) Color.Gray else if (esInvitado) (if (isDark) Color.DarkGray else Color.Black) else RosaYendo
                            ),
                            shape = RoundedCornerShape(16.dp),
                            enabled = botonHabilitado
                        ) {
                            Text(
                                text = textoBoton,
                                fontWeight = FontWeight.Bold,
                                fontSize = 16.sp,
                                color = Color.White
                            )
                        }
                    }
                }
            }
        ) { padding ->
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentPadding = PaddingValues(16.dp)
            ) {
                item {
                    Text("Artículos", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                    Spacer(modifier = Modifier.height(12.dp))
                }

                if (CartManager.items.isEmpty()) {
                    item {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 40.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(Icons.Default.Remove, null, tint = colorTextoSecundario, modifier = Modifier.size(60.dp))
                            Spacer(Modifier.height(16.dp))
                            Text("Tu carrito está vacío", fontWeight = FontWeight.Bold, color = colorTextoSecundario)
                        }
                    }
                } else {
                    items(CartManager.items) { cartItem ->
                        ItemCarrito(
                            cartItem = cartItem,
                            onAumentar = {
                                val step = if (cartItem.producto.tipoVenta == "PESO") {
                                    if (cartItem.producto.pesoEstimadoKg > 0.0) cartItem.producto.pesoEstimadoKg else 0.25
                                } else 1.0
                                val nuevaCant = ((cartItem.cantidad + step) * 1000.0).roundToInt() / 1000.0
                                CartManager.actualizarCantidad(cartItem, nuevaCant)
                            },
                            onDisminuir = {
                                val step = if (cartItem.producto.tipoVenta == "PESO") {
                                    if (cartItem.producto.pesoEstimadoKg > 0.0) cartItem.producto.pesoEstimadoKg else 0.25
                                } else 1.0
                                if (cartItem.cantidad > step + 0.001) {
                                    val nuevaCant = ((cartItem.cantidad - step) * 1000.0).roundToInt() / 1000.0
                                    CartManager.actualizarCantidad(cartItem, nuevaCant)
                                } else {
                                    CartManager.eliminarItem(cartItem)
                                }
                            }
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                    }
                }

                // ====================================================================
                // 🚀 UI DE REEMPLAZOS (Solo si es comercio)
                // ====================================================================


                if (CartManager.items.isNotEmpty()) {
                    item {
                        if (hayAgotados) {
                            Card(
                                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                                colors = CardDefaults.cardColors(containerColor = Color(0xFF3E1212)),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Default.Warning, null, tint = Color.Red)
                                    Spacer(Modifier.width(12.dp))
                                    Text("Hay artículos agotados en tu carrito. Elimínalos para continuar.", color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.Bold)
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(24.dp))
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            colors = CardDefaults.cardColors(
                                containerColor = if (esInvitado) {
                                    if (isDark) Color.White.copy(alpha = 0.05f) else Color.Black.copy(alpha = 0.05f)
                                } else {
                                    RosaYendo.copy(alpha = 0.1f)
                                }
                            ),
                            shape = RoundedCornerShape(12.dp),
                            elevation = CardDefaults.cardElevation(0.dp)
                        ) {
                            Text(
                                text = if (esInvitado)
                                    "Crea una cuenta para finalizar tu pedido y disfrutar de Yendo Mx."
                                else "Podrás elegir envío, recolección en tienda y aplicar cupones en el siguiente paso.",
                                modifier = Modifier.padding(16.dp),
                                color = if (esInvitado) colorTextoPrincipal else RosaYendo,
                                fontSize = 13.sp,
                                textAlign = TextAlign.Center,
                                fontWeight = FontWeight.Medium
                            )
                        }
                        Spacer(modifier = Modifier.height(80.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun ItemCarrito(cartItem: CartItem, onAumentar: () -> Unit, onDisminuir: () -> Unit) {
    val producto = cartItem.producto
    val cantidad = cartItem.cantidad

    val isDark = isSystemInDarkTheme()
    val colorFondoTarjeta = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorFondoImagen = Color.Transparent
    val colorFondoControles = if (isDark) Color(0xFF3A3A3C) else Color(0xFFF5F5F5)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    val step = remember(cartItem.producto.tipoVenta, cartItem.producto.pesoEstimadoKg) {
        if (cartItem.producto.tipoVenta == "PESO") {
            if (cartItem.producto.pesoEstimadoKg > 0.0) cartItem.producto.pesoEstimadoKg else 0.25
        } else {
            1.0
        }
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = colorFondoTarjeta),
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(1.dp)
    ) {
        Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {

            Box(
                modifier = Modifier
                    .size(60.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(colorFondoImagen)
            ) {
                if (producto.foto.isNotEmpty()) {
                    AsyncImage(
                        model = ImageRequest.Builder(LocalPlatformContext.current)
                            .data(producto.foto)
                            .size(Size(200, 200))
                            .crossfade(true)
                            .build(),
                        contentDescription = producto.nombre,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Text("Yendo", modifier = Modifier.align(Alignment.Center), fontSize = 10.sp, color = colorTextoSecundario, fontWeight = FontWeight.Bold)
                }
            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(producto.nombre, fontWeight = FontWeight.Bold, fontSize = 15.sp, maxLines = 1, color = colorTextoPrincipal)

                if (cartItem.adicionales.isNotEmpty()) {
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text("+ ", color = colorTextoSecundario, fontSize = 12.sp)
                        cartItem.adicionales.forEachIndexed { index, adic ->
                            val colorAdic = if (adic.disponible) colorTextoSecundario else Color.Red
                            val sufijo = if (index < cartItem.adicionales.size - 1) ", " else ""
                            Text(
                                text = "${adic.nombre}${sufijo}", 
                                color = colorAdic, 
                                fontSize = 12.sp, 
                                fontWeight = if (adic.disponible) FontWeight.Normal else FontWeight.Bold
                            )
                        }
                    }
                } else if (producto.descripcion.isNotEmpty()) {
                    Text(producto.descripcion, color = colorTextoSecundario, fontSize = 12.sp, maxLines = 1)
                }

                if (!producto.disponible) {
                    Text("AGOTADO / NO DISPONIBLE", color = Color.Red, fontSize = 11.sp, fontWeight = FontWeight.Black)
                }

                if (cartItem.notas.isNotBlank()) {
                    Text("Nota: ${cartItem.notas}", color = RosaYendo.copy(alpha = 0.8f), fontSize = 12.sp, fontStyle = FontStyle.Italic, maxLines = 2)
                }

                // 🚀 PRECIO DINÁMICO EN LA UI
                val precioUnitarioVigente = cartItem.precioUnitarioVigente
                
                val etiquetaPrecio = if (producto.precioPor == "LITRO") "L" else "kg"
                val textoPrecioReferencia = when (producto.tipoVenta) {
                    "PIEZA_POR_PESO", "PESO" -> "$${formatDecimal(producto.precio, 2)} / $etiquetaPrecio"
                    else -> "$${formatDecimal(producto.precio, 2)} / un."
                }

                Text(
                    text = textoPrecioReferencia,
                    color = Color.Gray,
                    fontSize = 11.sp
                )

                Text(
                    text = "$${formatDecimal(cartItem.precioTotal, 2)}", // 🚀 ESTO OBLIGARÁ A MOSTRAR LOS $10.00 REALES
                    fontWeight = FontWeight.ExtraBold,
                    fontSize = 16.sp,
                    color = RosaYendo
                )

                // 🚀 ETIQUETA INTELIGENTE (Descuento vs Mayoreo)
                if (precioUnitarioVigente < producto.precio) {
                    val textoEtiqueta = if (CartManager.restauranteActualTipoNegocio == "COMERCIO" || CartManager.restauranteActualTipoNegocio == "TIENDITA") "Precio Mayoreo" else "Descuento aplicado"
                    Text(textoEtiqueta, color = Color(0xFF00BFA5), fontWeight = FontWeight.Bold, fontSize = 10.sp)
                }
            }

            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.background(colorFondoControles, RoundedCornerShape(12.dp))) {
                IconButton(onClick = onDisminuir, modifier = Modifier.size(32.dp)) {
                    if (cantidad <= step) {
                        Icon(Icons.Default.Delete, null, tint = colorTextoSecundario, modifier = Modifier.size(16.dp))
                    } else {
                        Icon(Icons.Default.Remove, null, tint = colorTextoPrincipal, modifier = Modifier.size(16.dp))
                    }
                }

                val textoCantidad = when (cartItem.producto.tipoVenta) {
                    "PESO" -> {
                        // 🚀 Lógica Dual: Peso vs Volumen
                        val esVolumen = cartItem.producto.precioPor == "LITRO"
                        val sufijoMenor = if (esVolumen) "ml" else "g"
                        val sufijoMayor = if (esVolumen) "L" else "kg"

                        val subUnidad = (cartItem.cantidad * 1000).roundToInt()
                        if (subUnidad < 1000) "$subUnidad $sufijoMenor"
                        else "${formatDecimal(cartItem.cantidad, 2)} $sufijoMayor"
                    }
                    "PIEZA_POR_PESO" -> {
                        if (cartItem.modoCompra == "PESO") {
                            // 🚀 Lógica Dual: Peso vs Volumen
                            val esVolumen = cartItem.producto.precioPor == "LITRO"
                            val sufijoMenor = if (esVolumen) "ml" else "g"
                            val sufijoMayor = if (esVolumen) "L" else "kg"

                            val subUnidadTotal = (cartItem.producto.pesoEstimadoKg * cartItem.cantidad * 1000).roundToInt()
                            if (subUnidadTotal < 1000) "$subUnidadTotal $sufijoMenor"
                            else "${formatDecimal(subUnidadTotal / 1000.0, 2)} $sufijoMayor"
                        } else {
                            "${cartItem.cantidad.toInt()} un."
                        }
                    }
                    else -> {
                        if (cartItem.cantidad % 1.0 == 0.0) cartItem.cantidad.toInt().toString() else formatDecimal(cartItem.cantidad, 2)
                    }
                }

                Text(
                    text = textoCantidad,
                    fontWeight = FontWeight.Bold,
                    fontSize = 13.sp,
                    color = colorTextoPrincipal,
                    modifier = Modifier.padding(horizontal = 4.dp)
                )

                IconButton(onClick = onAumentar, modifier = Modifier.size(32.dp)) {
                    Icon(Icons.Default.Add, null, tint = RosaYendo, modifier = Modifier.size(16.dp))
                }
            }
        }
    }
}